/**
 * Single source of truth for checklist progress calculations.
 * Every component that displays checklist counts MUST use these helpers
 * to guarantee identical numbers across the UI.
 *
 * Key rules:
 * - mgmtRequired / mgmtChecked: only items where requires_management_check !== false
 * - subRequired / subChecked: only items where requires_subcontractor_check === true
 * - finalCompleted / finalTotal: ALL items — an item is "final complete" when all
 *   required checks for that item are done (mgmt if required, sub if required)
 * - overallPct: finalCompleted / finalTotal (used for combined project progress)
 */

export interface ChecklistProgress {
  mgmtRequired: number;
  mgmtChecked: number;
  subRequired: number;
  subChecked: number;
  finalCompleted: number;
  finalTotal: number;
  mgmtPct: number;
  subPct: number;
  overallPct: number;
  subWaiting: boolean;
}

export function computeChecklistProgress(
  items: Array<{
    requires_management_check?: boolean;
    management_checked: boolean;
    requires_subcontractor_check?: boolean;
    subcontractor_checked: boolean;
  }>
): ChecklistProgress {
  let mgmtRequired = 0;
  let mgmtChecked = 0;
  let subRequired = 0;
  let subChecked = 0;
  let finalCompleted = 0;

  for (const i of items) {
    const needsMgmt = i.requires_management_check !== false;
    const needsSub = i.requires_subcontractor_check === true;

    // Management counts: only items that require management check
    if (needsMgmt) {
      mgmtRequired++;
      if (i.management_checked) mgmtChecked++;
    }

    // Subcontractor counts: only items that require subcontractor check
    if (needsSub) {
      subRequired++;
      if (i.subcontractor_checked) subChecked++;
    }

    // Final approval: item is complete when all required checks are done
    const mgmtOk = !needsMgmt || i.management_checked;
    const subOk = !needsSub || i.subcontractor_checked;
    if (mgmtOk && subOk) {
      finalCompleted++;
    }
  }

  const finalTotal = items.length;
  return {
    mgmtRequired,
    mgmtChecked,
    subRequired,
    subChecked,
    finalCompleted,
    finalTotal,
    mgmtPct: mgmtRequired > 0 ? Math.round((mgmtChecked / mgmtRequired) * 100) : (items.length > 0 ? 100 : 0),
    subPct: subRequired > 0 ? Math.round((subChecked / subRequired) * 100) : 0,
    overallPct: finalTotal > 0 ? Math.round((finalCompleted / finalTotal) * 100) : 0,
    subWaiting: subRequired > 0 && subChecked < subRequired,
  };
}

export function isChecklistItemComplete(item: {
  requires_management_check?: boolean;
  requires_subcontractor_check?: boolean;
  management_checked: boolean;
  subcontractor_checked: boolean;
}): boolean {
  const needsMgmt = item.requires_management_check !== false;
  const needsSub = item.requires_subcontractor_check === true;
  return (!needsMgmt || item.management_checked) && (!needsSub || item.subcontractor_checked);
}

/**
 * Compute per-stage checklist progress from a subset of checklist items.
 * Used by stage cards to show stage-level final approval %.
 */
export function computeStageChecklistProgress(
  items: Array<{
    requires_management_check?: boolean;
    management_checked: boolean;
    requires_subcontractor_check?: boolean;
    subcontractor_checked: boolean;
  }>
): { finalCompleted: number; finalTotal: number; finalPct: number } {
  let finalCompleted = 0;
  const finalTotal = items.length;
  for (const i of items) {
    const needsMgmt = i.requires_management_check !== false;
    const needsSub = i.requires_subcontractor_check === true;
    const mgmtOk = !needsMgmt || i.management_checked;
    const subOk = !needsSub || i.subcontractor_checked;
    if (mgmtOk && subOk) finalCompleted++;
  }
  return {
    finalCompleted,
    finalTotal,
    finalPct: finalTotal > 0 ? Math.round((finalCompleted / finalTotal) * 100) : 0,
  };
}